import { useEffect, useState } from 'react';
import type { AdminSettings } from '../lib/api';
import { getSettings } from '../lib/api';
import { logout } from '../lib/auth';
import { IconRefresh, IconCheck, IconClose } from '../components/icons';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../lib/toast';

export function SettingsPage() {
  const [data, setData] = useState<AdminSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  async function refresh() {
    setLoading(true);
    try {
      const res = await getSettings();
      setData(res);
    } catch (e) {
      toast.error('Failed to load settings', e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <section className="px-8 py-7 max-w-[900px]">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="h-display text-[26px]">Settings</h1>
          <p className="text-[13.5px] text-ink-muted mt-0.5">System info, admin allowlist, and environment health.</p>
        </div>
        <button onClick={refresh} className="h-9 w-9 grid place-items-center rounded-md border border-line bg-white hover:bg-cream-tint" title="Refresh">
          <IconRefresh className={'w-4 h-4 text-ink-muted ' + (loading ? 'animate-spin' : '')} />
        </button>
      </div>

      {/* Profile */}
      <Section title="Profile" hint="From your Netlify Identity session">
        {loading && !data && <Skeleton className="h-12 w-full" />}
        {data && (
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-navy text-white grid place-items-center font-display text-[16px]">
              {(data.user.name || data.user.email).slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-ink truncate">{data.user.name || data.user.email}</div>
              <div className="text-[12.5px] text-ink-muted truncate">{data.user.email}</div>
            </div>
            <button onClick={() => logout()} className="h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint">
              Sign out
            </button>
          </div>
        )}
      </Section>

      {/* Admin allowlist */}
      <Section title="Admin allowlist" hint="Configured via ADMIN_ALLOWED_EMAILS env var on Netlify. Add or remove emails in Site configuration -> Environment variables, then redeploy.">
        {loading && !data && <Skeleton className="h-8 w-72" />}
        {data && (
          <div className="space-y-2">
            {data.admins.emails.length === 0 && <div className="text-ink-muted text-[13px]">No admins configured.</div>}
            {data.admins.emails.map(email => (
              <div key={email} className="flex items-center gap-2 text-[13px]">
                <span className="w-1.5 h-1.5 rounded-full bg-funds inline-block" />
                <span className="font-mono">{email}</span>
                {email.toLowerCase() === data.user.email.toLowerCase() && (
                  <span className="text-[11px] px-1.5 py-px rounded-full bg-cream-deep text-navy">you</span>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Env health */}
      <Section title="Environment health" hint="Required env vars on Netlify. Green = configured, red = missing.">
        {loading && !data && (
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-7" />)}
          </div>
        )}
        {data && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
            <EnvRow label="Airtable token + base id"   on={data.env.airtable}      required />
            <EnvRow label="Identity allowlist"          on={data.env.identity}      required />
            <EnvRow label="Netlify build hook"          on={data.env.buildHook}     required />
            <EnvRow label="Restore HMAC secret"         on={data.env.restoreSecret} required />
            <EnvRow label="Resend (transactional mail)" on={data.env.resend} />
            <EnvRow label="Anthropic API (news cron)"   on={data.env.anthropic} />
            <EnvRow label="Slack webhook (notifs)"      on={data.env.slack} />
          </div>
        )}
      </Section>

      {/* Tables + sites */}
      <Section title="Connected resources">
        {loading && !data && <Skeleton className="h-20 w-full" />}
        {data && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px]">
            <KV label="Public site"      value={<a className="text-navy underline underline-offset-4 decoration-line" href={data.site.primary} target="_blank" rel="noreferrer">{data.site.primary}</a>} />
            <KV label="Admin site"       value={<span className="font-mono text-[12.5px]">{data.site.admin}</span>} />
            <KV label="Funds table"      value={<span className="font-mono text-[12.5px]">{data.tables.funds}</span>} />
            <KV label="Founders table"   value={<span className="font-mono text-[12.5px]">{data.tables.founders}</span>} />
            <KV label="News table"       value={<span className="font-mono text-[12.5px]">{data.tables.news}</span>} />
            <KV label="Airtable base"    value={<a className="text-navy underline underline-offset-4 decoration-line" href="https://airtable.com/appQjEfx3OTWCnpwd" target="_blank" rel="noreferrer">Open in Airtable</a>} />
          </div>
        )}
      </Section>

      <Section title="Keyboard shortcuts">
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
          <Shortcut keys={['⌘', 'K']} label="Open command palette" />
          <Shortcut keys={['Esc']} label="Close drawer or palette" />
          <Shortcut keys={['↑', '↓']} label="Navigate command palette" />
          <Shortcut keys={['↵']} label="Run selected command" />
        </div>
      </Section>
    </section>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-line rounded-lg shadow-card p-5 mb-4">
      <div className="mb-3">
        <h2 className="h-display text-[15px]">{title}</h2>
        {hint && <p className="text-[12px] text-ink-muted mt-0.5">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function EnvRow({ label, on, required = false }: { label: string; on: boolean; required?: boolean }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <span className={'w-5 h-5 rounded-full grid place-items-center shrink-0 ' + (on ? 'bg-funds-soft text-funds-deep' : (required ? 'bg-red-100 text-red-700' : 'bg-cream-deep text-ink-muted'))}>
        {on ? <IconCheck className="w-3 h-3" /> : <IconClose className="w-3 h-3" />}
      </span>
      <span className="text-ink truncate">{label}</span>
      {!on && required && <span className="text-[11px] text-red-700">required</span>}
      {!on && !required && <span className="text-[11px] text-ink-faint">optional</span>}
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="font-medium truncate">{value}</div>
    </div>
  );
}

function Shortcut({ keys, label }: { keys: string[]; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd key={i} className="font-mono text-[11px] bg-cream-tint border border-line rounded px-1.5 py-0.5">{k}</kbd>
        ))}
      </span>
      <span className="text-ink-muted">{label}</span>
    </div>
  );
}
